import { useAuth } from "@clerk/clerk-react";
import { Box, ButtonBase, IconButton, Typography } from "@mui/material";
import { getMarkRange } from "@tiptap/core";
import { Editor as TipTapEditor } from "@tiptap/react";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";

import { OrganizationTemplateRubricSuggestion } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";

import { OrganizationTemplateRubricSuggestionType } from "./schema";
import styles from "./styles";

interface SentenceEditSuggestionPopupProps {
  editor: TipTapEditor;
  suggestion: OrganizationTemplateRubricSuggestion;
  onApplied?: (suggestionId: number, beforeHtml: string) => void;
  onDismiss: () => void | Promise<void>;
}

type TypeConfig = {
  kindLabel: string;
  primaryLabel: string;
  whyText: string;
};

const TYPE_CONFIG: Record<string, TypeConfig> = {
  call_to_action: {
    kindLabel: "Call to action",
    primaryLabel: "Add a clear ask",
    whyText:
      "A specific, low-effort ask (reply to this, want me to send ideas?) tends to lift reply rates over open-ended closers — worth editing this in.",
  },
  opening_hook: {
    kindLabel: "Opening hook",
    primaryLabel: "Use this hook",
    whyText:
      "Openers that reference something specific to the brand (a recent post, a launch, a product line) tend to lift reply rates over generic greetings — brand managers can tell within the first sentence whether you actually know their brand, so it's worth editing this in.",
  },
};

const DEFAULT_CONFIG: TypeConfig = {
  kindLabel: "Suggestion",
  primaryLabel: "Apply",
  whyText: "",
};

const SentenceEditSuggestionPopup = ({
  editor,
  suggestion,
  onApplied,
  onDismiss,
}: SentenceEditSuggestionPopupProps) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [whyOpen, setWhyOpen] = useState(false);
  const [hookText, setHookText] = useState(
    suggestion.proposedReplacement ?? "",
  );

  useEffect(() => {
    setHookText(suggestion.proposedReplacement ?? "");
  }, [suggestion.id, suggestion.proposedReplacement]);

  const config = TYPE_CONFIG[suggestion.type] ?? DEFAULT_CONFIG;
  const isOpeningHook =
    suggestion.type === OrganizationTemplateRubricSuggestionType.opening_hook;
  const hasReplacement = isOpeningHook
    ? Boolean(hookText.trim())
    : Boolean(suggestion.proposedReplacement?.trim());

  const handleApply = async (): Promise<void> => {
    const replacement = isOpeningHook
      ? hookText.trim()
      : suggestion.proposedReplacement?.trim();
    if (!replacement || !currentOrg?.id) return;

    const highlightMark = editor.state.schema.marks.highlight;
    if (!highlightMark) return;
    const { from } = editor.state.selection;
    const $pos = editor.state.doc.resolve(from);
    const range = getMarkRange($pos, highlightMark);
    if (!range) return;

    const beforeHtml = editor.getHTML();

    editor
      .chain()
      .focus()
      .setTextSelection({ from: range.from, to: range.to })
      .insertContent(replacement)
      .run();

    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg.id}/organization-template-rubric-suggestions/suggestion/${suggestion.id}`,
      "PATCH",
      {},
      { status: "applied" },
    );

    onApplied?.(suggestion.id, beforeHtml);
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.head}>
        <Box sx={styles.avatar}>
          <Box
            component="i"
            className="fa-solid fa-sparkles"
            sx={styles.avatarIcon}
          />
        </Box>
        <Box sx={styles.helperBlock}>
          <Typography sx={styles.helperText}>Bento helper</Typography>
          <Box sx={styles.kindRow}>
            <Box component="span" sx={styles.kindDot} />
            Tip · {config.kindLabel}
          </Box>
        </Box>
        <IconButton size="small" sx={styles.closeButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.closeIcon}
          />
        </IconButton>
      </Box>

      <Box sx={styles.body}>
        {suggestion.title && (
          <Typography sx={styles.title}>{suggestion.title}</Typography>
        )}
        <Typography sx={styles.rationale}>{suggestion.body}</Typography>

        {isOpeningHook && (
          <>
            <Typography sx={styles.fieldLabel}>Your opening hook</Typography>
            <Box sx={styles.textareaWrapper}>
              <Box
                component="textarea"
                value={hookText}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setHookText(e.target.value)
                }
                placeholder="Write an opener that makes you stand out..."
                sx={styles.textarea}
              />
            </Box>
          </>
        )}

        {!isOpeningHook && hasReplacement && (
          <Box sx={styles.diffCard}>
            <Box sx={styles.diffRow}>
              <Box component="span" sx={styles.diffMarkerDel}>
                From
              </Box>
              <Box component="span" sx={styles.diffTextDel}>
                {suggestion.anchorText}
              </Box>
            </Box>
            <Box sx={styles.diffRow}>
              <Box component="span" sx={styles.diffMarkerAdd}>
                To
              </Box>
              <Box component="span" sx={styles.diffTextAdd}>
                {suggestion.proposedReplacement}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {whyOpen && config.whyText && (
        <Box sx={styles.whyBlock}>
          <Typography sx={styles.whyHead}>Why this matters</Typography>
          {config.whyText}
        </Box>
      )}

      <Box sx={styles.actions}>
        {hasReplacement && (
          <ButtonBase sx={styles.primaryButton} onClick={handleApply}>
            <Box
              component="i"
              className="fa-solid fa-check"
              sx={styles.primaryIcon}
            />
            {config.primaryLabel}
          </ButtonBase>
        )}
        <ButtonBase sx={styles.rejectButton} onClick={onDismiss}>
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.rejectIcon}
          />
        </ButtonBase>
        <ButtonBase
          sx={styles.whyButton}
          onClick={() => setWhyOpen((prev) => !prev)}
        >
          <Box
            component="i"
            className="fa-regular fa-circle-info"
            sx={styles.whyIcon}
          />
          {whyOpen ? "Hide" : "Why?"}
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default SentenceEditSuggestionPopup;
